import type ExcelJS from 'exceljs';

// exceljs is heavy — load it on demand so it never weighs down the main bundle.
async function xlsx(): Promise<typeof import('exceljs').default> {
  return (await import('exceljs')).default;
}

// Symbol Quantity Excel (§5.6). Branded teal/gold (NEVER navy). A hidden Row-ID
// column keys a clean re-import by ID (never by position). The QTY column is the
// ONLY thing a human fills — the software never counts. Per-system subtotals and
// a grand total are SUM formulas over the QTY cells.

export interface CountSheetRow {
  symbol_id: string; system_name: string | null; symbol_tag: string | null; legend_description: string | null;
  device_type: string | null; spec_reference: string | null; sheets_present: string | null;
  assembly_hint: string | null; confidence: number | null; map_status: string | null; count: number | null;
}

const TEAL = 'FF0E7C86', TEAL_DARK = 'FF0A5C64', TEAL_LIGHT = 'FFE2F1F2';
const GOLD = 'FFC9A227', GOLD_LIGHT = 'FFF7EFD2', WHITE = 'FFFFFFFF', BLACK = 'FF111111';
const GRAY = 'FFEDEFEF', GRAY_500 = 'FF7A8585', DANGER = 'FFB3261E';
const LAST_COL = 13;     // M
const QTY_COL = 10;      // J
const HEADER_ROW = 6;    // table header; data begins at row 7
const thin = { style: 'thin' as const, color: { argb: 'FFCDD3D3' } };
const border = { top: thin, left: thin, bottom: thin, right: thin };

function sheetsToText(json: string | null): string {
  try { const a = JSON.parse(json ?? '[]'); return Array.isArray(a) ? a.filter(Boolean).join(', ') : ''; } catch { return ''; }
}

export async function generateCountSheet(project: { id: string; name: string }, rows: CountSheetRow[], today: string): Promise<Blob> {
  const ExcelJS = await xlsx();
  const wb = new ExcelJS.Workbook();
  wb.creator = '3D Technology Services, Inc.';
  const ws = wb.addWorksheet('Symbol Quantities', { views: [{ state: 'frozen', ySplit: HEADER_ROW }] });

  ws.columns = [
    { width: 4 }, { width: 4 }, { width: 26 }, { width: 10 }, { width: 34 }, { width: 24 },
    { width: 15 }, { width: 16 }, { width: 38 }, { width: 10 }, { width: 24 }, { width: 11 }, { width: 13 },
  ];
  ws.getColumn(1).hidden = true;   // Row ID
  ws.getColumn(2).hidden = true;   // Project ID

  const fill = (argb: string) => ({ type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb } });

  // Banner.
  ws.mergeCells(1, 1, 1, LAST_COL);
  const t = ws.getCell(1, 1);
  t.value = '3D Technology Services, Inc.   ·   3dtsi.com';
  t.font = { bold: true, size: 14, color: { argb: WHITE } }; t.fill = fill(TEAL);
  t.alignment = { vertical: 'middle', indent: 1 }; ws.getRow(1).height = 26;

  ws.mergeCells(2, 1, 2, LAST_COL);
  const sub = ws.getCell(2, 1);
  sub.value = 'Symbol Quantity Count Sheet'; sub.font = { bold: true, size: 12, color: { argb: BLACK } };
  sub.fill = fill(GOLD); sub.alignment = { vertical: 'middle', indent: 1 }; ws.getRow(2).height = 20;

  ws.mergeCells(3, 1, 3, LAST_COL);
  ws.getCell(3, 1).value = `Project: ${project.name}`;
  ws.getCell(3, 1).font = { bold: true, color: { argb: BLACK } };

  ws.mergeCells(4, 1, 4, LAST_COL);
  const ins = ws.getCell(4, 1);
  ins.value = 'Enter a whole number in the QTY column for each symbol. The software does NOT count — quantities are human-entered. Do not edit hidden columns or row order; re-import matches by hidden Row ID.';
  ins.font = { italic: true, color: { argb: GRAY_500 } }; ins.alignment = { wrapText: true };
  ws.getRow(4).height = 28;

  // Table header.
  const headers = ['Row ID', 'Project ID', 'System category', 'Symbol', 'Description', 'Device type',
    'Spec ref', 'Sheets', 'Suggested part family', 'QTY', 'Comments', 'Confidence', 'Needs review'];
  const hr = ws.getRow(HEADER_ROW);
  headers.forEach((h, i) => {
    const cell = hr.getCell(i + 1);
    cell.value = h; cell.font = { bold: true, color: { argb: WHITE } };
    cell.fill = fill(TEAL_DARK); cell.alignment = { wrapText: true, vertical: 'middle' }; cell.border = border;
  });
  hr.height = 30;

  // Data grouped by system, with per-system subtotals.
  let r = HEADER_ROW + 1;
  let groupStart = r;
  let currentSys: string | null = null;
  const subtotalRows: number[] = [];

  const writeSubtotal = (sys: string, start: number, end: number) => {
    const row = ws.getRow(r);
    row.getCell(3).value = `Subtotal — ${sys}`;
    for (let c = 1; c <= LAST_COL; c++) { row.getCell(c).fill = fill(GRAY); row.getCell(c).font = { bold: true }; }
    if (end >= start) row.getCell(QTY_COL).value = { formula: `SUM(J${start}:J${end})` };
    else row.getCell(QTY_COL).value = 0;
    subtotalRows.push(r); r++;
  };

  for (const row of rows) {
    if (currentSys !== null && row.system_name !== currentSys) {
      writeSubtotal(currentSys, groupStart, r - 1);
      groupStart = r;
    }
    currentSys = row.system_name;
    const dr = ws.getRow(r);
    dr.getCell(1).value = row.symbol_id;
    dr.getCell(2).value = project.id;
    dr.getCell(3).value = row.system_name ?? '';
    dr.getCell(4).value = row.symbol_tag ?? '';
    dr.getCell(5).value = row.legend_description ?? '';
    dr.getCell(6).value = row.device_type ?? '';
    dr.getCell(7).value = row.spec_reference ?? '';
    dr.getCell(8).value = sheetsToText(row.sheets_present);
    dr.getCell(9).value = row.assembly_hint ?? '';
    if (row.count != null) dr.getCell(QTY_COL).value = row.count;     // round-trip prior counts
    dr.getCell(12).value = row.confidence != null ? Math.round(row.confidence * 100) / 100 : '';
    dr.getCell(13).value = row.map_status === 'needs_review' ? 'REVIEW' : '';

    const qty = dr.getCell(QTY_COL);
    qty.fill = fill(GOLD_LIGHT); qty.border = border;
    qty.dataValidation = {
      type: 'whole', operator: 'greaterThanOrEqual', formulae: [0], allowBlank: true,
      showErrorMessage: true, errorStyle: 'error', errorTitle: 'Invalid quantity',
      error: 'Enter a whole number (0 or greater). The software never counts for you.',
    };
    if (row.map_status === 'needs_review') dr.getCell(13).font = { bold: true, color: { argb: DANGER } };
    for (const c of [3, 4, 5, 6, 7, 8, 9, 11]) dr.getCell(c).border = border;
    r++;
  }
  if (currentSys !== null) writeSubtotal(currentSys, groupStart, r - 1);

  // Grand total.
  const gr = ws.getRow(r);
  gr.getCell(3).value = 'GRAND TOTAL';
  for (let c = 1; c <= LAST_COL; c++) { gr.getCell(c).fill = fill(TEAL_LIGHT); gr.getCell(c).font = { bold: true, size: 12, color: { argb: TEAL_DARK } }; }
  gr.getCell(QTY_COL).value = subtotalRows.length ? { formula: subtotalRows.map((n) => `J${n}`).join('+') } : 0;
  r += 2;

  // Merge from column 3 (the hidden Row-ID/Project-ID columns stay genuinely
  // empty, so re-import never mistakes the footer for a data row).
  ws.mergeCells(r, 3, r, LAST_COL);
  const foot = ws.getCell(r, 3);
  foot.value = `Generated ${today} · 3D Technology Services, Inc. · 3dtsi.com · Quantities are human-entered. The software never counts.`;
  foot.font = { italic: true, size: 9, color: { argb: GRAY_500 } };

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// Read a completed sheet back. Returns one record per DATA row (rows that carry a
// hidden Row ID); subtotal/total/footer rows have no Row ID and are skipped.
export interface ParsedCount { symbol_id: string; qty: string; comments: string }

export async function parseCountSheet(file: File): Promise<ParsedCount[]> {
  const ExcelJS = await xlsx();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) throw new Error('No worksheet found in the file.');

  const out: ParsedCount[] = [];
  ws.eachRow((row, rowNumber) => {
    if (rowNumber <= HEADER_ROW) return;
    const id = cellText(row.getCell(1));
    const projId = cellText(row.getCell(2));
    // A data row carries BOTH a hidden Row ID and Project ID. Subtotal, grand-total
    // and footer rows have neither in column 2, so they are skipped cleanly.
    if (!id || !projId) return;
    out.push({ symbol_id: id, qty: cellNumberText(row.getCell(QTY_COL)), comments: cellText(row.getCell(11)) });
  });
  if (!out.length) throw new Error('No symbol rows found — is this the generated count sheet?');
  return out;
}

function cellText(cell: ExcelJS.Cell): string {
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object') {
    if ('text' in v && typeof (v as { text?: unknown }).text === 'string') return (v as { text: string }).text.trim();
    if ('result' in v) return `${(v as { result?: unknown }).result ?? ''}`.trim();
    if ('richText' in v) return (v as { richText: { text: string }[] }).richText.map((t) => t.text).join('').trim();
    return '';
  }
  return `${v}`.trim();
}

function cellNumberText(cell: ExcelJS.Cell): string {
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'number') return `${v}`;
  if (typeof v === 'object' && 'result' in v) return `${(v as { result?: unknown }).result ?? ''}`.trim();
  return `${v}`.trim();
}
